import { useEffect, useState } from "react";
import Application from "../../../../../types/admin/Application";
import { Button as SemanticButton } from "semantic-ui-react";
import {
    Form,
    Input,
    Checkbox,
    DatePicker,
    message,
    Modal,
    Card,
    Space,
    Button,
    Divider,
    Spin,
    Upload,
} from "antd";
import { UploadOutlined } from '@ant-design/icons';
import axios from "axios";
import { EmailPreviewer } from "../../../EmailTemplates";
import { EmailTemplate } from "../../../../../types/admin/EmailTemplate";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import type { UploadFile, UploadProps } from 'antd/es/upload/interface';
import { Rule } from "antd/es/form";
import useAuth from "../../../../../hooks/useAuth";

dayjs.extend(utc);
dayjs.extend(timezone);

// Define form values interface
interface EmailFormValues {
    to: string;
    cc: string[];
    bcc: string[];
    emailParams: Record<string, string>;
    enableSchedule: boolean;
    scheduledTime?: dayjs.Dayjs;
    attachments?: UploadFile[];
}

interface EmailSendProps {
    emailTemplate: EmailTemplate;
    application: Application;
    goBack: () => void;
}

const createFormData = (values: EmailFormValues, emailTemplate: EmailTemplate, application: Application, fileList: UploadFile[]) => {
    const formData = new FormData();
    const scheduledTime = values.scheduledTime?.tz("Asia/Hong_Kong", true) || dayjs();

    formData.append('emailData', JSON.stringify({
        emailId: emailTemplate.id,
        applicationId: application.id,
        cc: values.cc?.filter(email => email?.trim() !== "") || [],
        bcc: values.bcc?.filter(email => email?.trim() !== "") || [],
        emailParams: values.emailParams || {},
        scheduledTimeISO: scheduledTime.toISOString(),
    }));

    fileList.forEach(file => {
        const fileToUpload = file instanceof File ? file : file.originFileObj;
        if (fileToUpload) {
            formData.append('attachments', fileToUpload);
        }
    });

    return formData;
};

const EmailSend: React.FC<EmailSendProps> = ({ emailTemplate, application, goBack }) => {
    // Form and state
    const [form] = Form.useForm<EmailFormValues>();
    const [enableScheduleSend, setEnableScheduleSend] = useState(false);
    const [isPreviewModalVisible, setIsPreviewModalVisible] = useState(false);
    const [previewData, setPreviewData] = useState<EmailTemplate | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [fileList, setFileList] = useState<UploadFile[]>([]);
    const [ukTime, setUkTime] = useState<string>();
    const { email: contributorEmail } = useAuth('admin');

    const emailValidatorRule: Rule = {
        validator: async (_, value) => {
            if (!value || value.trim() === '') {
                return Promise.reject(new Error('Please enter a valid email or remove this field'));
            }

            const emails: string[] = [application.email, ...form.getFieldValue('cc'), ...form.getFieldValue('bcc')];
            const isDuplicate = emails.filter(email => (email?.trim() ?? '') === value.trim()).length > 1;
            if (isDuplicate) {
                return Promise.reject(new Error('Email addresses must be distinct'));
            }

            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value.trim())) {
                return Promise.reject(new Error('Please enter a valid email address'));
            }
            return Promise.resolve();
        }
    };

    // Upload configuration
    const MAX_FILE_SIZE_MB = 20;
    const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

    const uploadProps: UploadProps = {
        onRemove: (file) => {
            const newFileList = fileList.filter(f => f !== file);
            setFileList(newFileList);
        },
        beforeUpload: (file, _) => {
            if (file.size && file.size > MAX_FILE_SIZE_BYTES) {
                message.error(`${file.name} exceeds the maximum file size of ${MAX_FILE_SIZE_MB}MB.`);
                return Upload.LIST_IGNORE;
            }
            setFileList(prev => [...prev, file]);
            return false;
        },
        fileList,
    };

    // Form initial values
    const initialValues: EmailFormValues = {
        to: application.email,
        cc: [],
        bcc: [],
        enableSchedule: false,
        emailParams: emailTemplate.emailParamKeys.reduce((acc, key) => ({
            ...acc,
            [key]: ''
        }), {})
    };

    useEffect(() => {
        if (contributorEmail) {
            form.setFieldValue('bcc', [contributorEmail]);
        }
    }, [form, contributorEmail]);

    // Handlers
    const handlePreview = async () => {
        try {
            const values = await form.validateFields();
            setPreviewLoading(true);
            setIsPreviewModalVisible(true);

            const response = await axios.post(
                `/admin/email-template/preview/${application.id}`,
                {
                    emailId: emailTemplate.id,
                    emailParams: values.emailParams || {}
                }
            );

            setPreviewData(response.data);
        } catch (error) {
            message.error("Failed to generate email preview");
            setIsPreviewModalVisible(false);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleSend = async () => {
        try {
            const values = await form.validateFields();
            const scheduledTime = values.scheduledTime?.tz("Asia/Hong_Kong", true) || dayjs();

            Modal.confirm({
                title: "Are you sure you want to send this email?",
                content: enableScheduleSend
                    ? `This email will be scheduled to send at ${scheduledTime.toLocaleString()}`
                    : "This email will be sent immediately",
                async onOk() {
                    try {
                        const formData = createFormData(values, emailTemplate, application, fileList);

                        await axios.post(
                            `/admin/email-template/schedule/${application.id}`,
                            formData,
                            { headers: { 'Content-Type': 'multipart/form-data' } }
                        );

                        message.success(enableScheduleSend
                            ? `Email scheduled at ${scheduledTime.toLocaleString()}!`
                            : "Email sent!"
                        );
                        goBack();
                    } catch (error) {
                        message.error("Failed to send email. Please try again.");
                        console.error(error);
                    }
                },
            });
        } catch {
            message.error("Please check your form for errors");
        }
    };

    return (
        <div className="email-send-container">
            <Card className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h2>Send Email: {emailTemplate.id}</h2>
                    <Space>
                        <SemanticButton color="grey" onClick={goBack} icon="arrow left" content="Back" />
                        <SemanticButton color="green" onClick={handleSend} icon="send" content="Send" />
                        <SemanticButton color="purple" onClick={handlePreview} icon="eye" content="Preview" />
                    </Space>
                </div>

                <Form form={form} layout="vertical" initialValues={initialValues}>
                    <Card title="Recipient Information" className="mb-3">
                        <div>
                            To: <strong>{application.email}</strong>
                        </div>
                    </Card>

                    <Card title="CC/BCC" className="mb-3">
                        <div className="alert alert-warning border border-warning py-2 mb-3">
                            Warning: Please make sure that the email addresses in the Recipient, CC, and BCC fields are distinct.
                        </div>

                        <Divider />

                        <Form.List name="cc" initialValue={[]}>
                            {(fields, { add, remove }) => (
                                <>
                                    <h5>CC:</h5>
                                    {fields.map((field) => (
                                        <div key={field.key} style={{ display: 'flex', marginBottom: 8 }}>
                                            <Form.Item
                                                {...field}
                                                style={{ marginBottom: 0, flex: 1 }}
                                                rules={[emailValidatorRule]}
                                            >
                                                <Input placeholder="Email address" />
                                            </Form.Item>
                                            <SemanticButton
                                                icon="minus"
                                                negative
                                                basic
                                                size="tiny"
                                                onClick={() => remove(field.name)}
                                                style={{ marginLeft: 8 }}
                                            />
                                        </div>
                                    ))}
                                    <SemanticButton
                                        onClick={() => add()}
                                        icon="plus"
                                    >
                                        Add CC
                                    </SemanticButton>
                                </>
                            )}
                        </Form.List>

                        <Divider />

                        <Form.List name="bcc">

                            {(fields, { add, remove }) => (
                                <>
                                    <h5>BCC:</h5>
                                    <div className="alert alert-danger py-2 mb-3">
                                        Note: Your email address will be automatically BCC&apos;d in all outgoing emails.
                                        Please <b>DO NOT</b> include your email address again in the CC or BCC field.
                                    </div>
                                    {fields.map((field, index) => {
                                        const isContributorEmailField = index === 0;

                                        return (
                                            <div key={field.key} style={{ display: 'flex', marginBottom: 8 }}>
                                                <Form.Item
                                                    {...field}
                                                    style={{ marginBottom: 0, flex: 1 }}
                                                    rules={[emailValidatorRule]}
                                                >
                                                    <Input placeholder="Email address" disabled={isContributorEmailField} />
                                                </Form.Item>
                                                {!isContributorEmailField && (
                                                    <SemanticButton
                                                        icon="minus"
                                                        negative
                                                        basic
                                                        size="tiny"
                                                        onClick={() => remove(field.name)}
                                                        style={{ marginLeft: 8 }} />
                                                )}
                                            </div>
                                        );
                                    })}
                                    <SemanticButton
                                        onClick={() => add()}
                                        icon="plus"
                                    >
                                        Add BCC
                                    </SemanticButton>
                                </>
                            )}
                        </Form.List>
                    </Card>

                    {emailTemplate.emailParamKeys.length > 0 && (
                        <Card title="Email Parameters" className="mb-3">
                            {emailTemplate.emailParamKeys.map(key => (
                                <Form.Item
                                    key={key}
                                    label={`${key}:`}
                                    name={['emailParams', key]}
                                >
                                    <Input.TextArea placeholder={`Enter ${key}`} autoSize={{ minRows: 3 }} />
                                </Form.Item>
                            ))}
                        </Card>
                    )}

                    <Card title={
                        <Space>
                            <span>Attachments</span>
                        </Space>
                    } className="mb-3">
                        <div className="alert alert-warning py-2 mb-3">
                            Note: Maximum file size is 20MB.
                        </div>
                        <Form.Item name="attachments">
                            <Upload {...uploadProps} multiple>
                                <Button icon={<UploadOutlined />}>Select Files</Button>
                            </Upload>
                        </Form.Item>
                    </Card>

                    <Card title="Delivery Options">
                        <Form.Item name="enableSchedule" valuePropName="checked">
                            <Checkbox
                                onChange={(e) => setEnableScheduleSend(e.target.checked)}
                            >
                                Schedule send
                            </Checkbox>
                        </Form.Item>

                        {enableScheduleSend && (
                            <div>
                                <Form.Item
                                    name="scheduledTime"
                                    label={<p>Select date and time <b>in HKT</b>:</p>}
                                    rules={[{ required: true, message: 'Please select a date and time' }]}
                                >
                                    <DatePicker
                                        showTime
                                        format="YYYY-MM-DD HH:mm:ss"
                                        style={{ width: '100%' }}
                                        disabledDate={current => current && current.toDate() < new Date()}
                                        onChange={(date) => {
                                            if (date) {
                                                setUkTime(
                                                    date.tz("Asia/Hong_Kong", true) // Convert to HKT, keep the time as picked
                                                        .tz("Europe/London") // Convert to BST/GMT
                                                        .format("YYYY-MM-DD HH:mm:ss"));
                                            } else {
                                                setUkTime(undefined);
                                            }
                                        }}
                                    />
                                </Form.Item>
                                {ukTime &&
                                    <div style={{ marginTop: 4, fontSize: '12px', color: '#666' }}>
                                        Selected time in UK time (at that moment): {ukTime}
                                    </div>}
                            </div>
                        )}
                    </Card>
                </Form>
            </Card>

            <Modal
                open={isPreviewModalVisible}
                title="Email Preview"
                footer={[
                    <Button key="close" onClick={() => setIsPreviewModalVisible(false)}>
                        Close
                    </Button>
                ]}
                onCancel={() => setIsPreviewModalVisible(false)}
                width="80vw"
                style={{ height: "90vh", overflowY: "auto" }}
                centered
            >
                {previewLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Spin size="large" />
                        <p style={{ marginTop: 16 }}>Generating email preview...</p>
                    </div>
                ) : (
                    previewData && <EmailPreviewer values={previewData} />
                )}
            </Modal>
        </div>
    );
};

export default EmailSend;
